import { Injectable } from '@angular/core';
import { Subject, Observable, BehaviorSubject, map, catchError, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { AppService } from '../app.service';

import { Story } from '../shared/interfaces/story';
import { User } from '../shared/interfaces/user';
import { Notification } from '../shared/interfaces/notification';

export interface GetNotificationsCountResponse {
  messages: { exist: boolean; count: number }
  invitations: { exist: boolean; count: number }
  new_contacts: { exist: boolean; count: number }
  news: { exist: boolean; count: number }
}

export enum State {
  accepted = "accepted",
  refused = "refused",
  validated = "validated",
  rejected = "rejected",
  blocked = "blocked",
  blockedWithReport = "blocked_with_report"
}

@Injectable({
  providedIn: 'root',
})
export class HomeService {
  private stop$ = new Subject<void>()
  private start$ = new Subject<void>()

  toggleSidenav = new BehaviorSubject<boolean>(false)
  toggleEncounterSidenav = new BehaviorSubject<boolean>(false)
  pageDisplayed = new BehaviorSubject<string>('home')
  stories: Story[] = []
  story = new BehaviorSubject<Story | null>(null)
  encounters = new BehaviorSubject<User[]>([])
  dialogDisplayed = new BehaviorSubject<boolean>(false)
  notifications: Notification[] = []
  noMoreEncountersAvailable = new BehaviorSubject<boolean>(true)

  private photoLiked$ = new Subject<{ id: number; liked: boolean }>();
  liveNotifications$ = new BehaviorSubject<number | null>(null)
  messagesCount$ = new BehaviorSubject<number>(0)
  partialNotifications$ = new BehaviorSubject<number>(0)

  get liveNotifications(): Observable<number | null> {
    return this.liveNotifications$.asObservable()
  }

  get messagesCount(): Observable<number> {
    return this.messagesCount$.asObservable()
  }

  get partialNotifications(): Observable<number> {
    return this.partialNotifications$.asObservable()
  }

  constructor(
    private http: HttpClient,
    private appService: AppService,
  ) {}

  start(): void {
    this.start$.next();
  }

  stop(): void {
    this.stop$.next();
  }

  // to report a piece of content => the moderation team will review the piece of content
  createReportBetweenUsers(contentId: number, contentType: "story" | "user" | "photo" | "event" | "externallink"): Observable<string>{
    const body = {
      target: contentType,
      id: contentId
    }

    return this.http.post<{message: string}>(`${this.appService.API}/api/reports`, body).pipe(
      map(res => {
        console.log(res)
        return res.message
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    )
  }

  // "blocked_with_report" automatically reports the user => the moderation team will review the user
  updateRelationship(relatedUserId: number, state: State): Observable<string> {
    const body = {
      related_user_id: relatedUserId.toString(),
      state,
    }

    return this.http.post(`${this.appService.API}/api/relationship/easy`, body)
      .pipe(
        map(() => {
          return `User ${relatedUserId} has been successfully ${state}`
        }),
        catchError(error => {
          return throwError(() => error)
        })
      )
  }

  deleteRelationship(relatedUserId: number): Observable<string> {
    return this.http.delete(`${this.appService.API}/api/relationship?related_user_id=${relatedUserId}`)
      .pipe(
        map(() => {
          return `User ${relatedUserId} has been successfully deleted`
        }),
        catchError(error => {
          return throwError(() => error)
        })
      )
  }

  getNotificationsCount(): Observable<{crush?: number, invitation?: number, contact?: number, suggestion?: number}>{
    return this.http
      .get<{notifications:{crush?: number, invitation?: number, contact?: number, suggestion?: number}}>(`${this.appService.API}/api/notifications?types=crush,suggestion,contact`)
      .pipe(
        map(res => {
          return res.notifications
        }),
        catchError(error => {
          return throwError(() => error)
        })
      )
  }

  getNotificationsList(): Observable<Notification[]> {
    return this.http
      .get<{ notifications: Notification[] }>(`${this.appService.API}/api/notifications/details/1/30`)
      .pipe(
        map((res) => {
          res.notifications.map(n => {
            if(n.type === 'invitation') {
              const index = res.notifications.indexOf(n)
              res.notifications.splice(index, 1)
            }
          })

          return res.notifications
        }),
        catchError((error) => {
          return throwError(() => error)
        })
      )
  }
}
